import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  JourneyPlanner,
  LiveDepartures,
  Timetables,
  Tabs,
  Tab,
} from './components';
import {
  DEFAULT_JP_URL, DEFAULT_LD_URL, DEFAULT_TT_URL, DEFAULT_DT_URL,
} from './utils/Config';
import {
  getJourneyPlannerUrl,
  getLiveDeparturesUrl,
  getTimetablesUrl,
  getDigitalTimetablesUrl,
} from './utils/UrlGenerator';
import { initFeatureFlagService } from './services/FeatureFlagService';
import { initAliasMacronService } from './services/AliasMacronService';

initFeatureFlagService(process.env.REACT_APP_LD_CLIENT_SIDE_KEY);
initAliasMacronService();

function App({
  jpUrl, ldUrl, ttUrl, dtUrl, isJpPreviewOn,
}) {
  const journeyPlanner = 'journeyPlanner';
  const liveDepartures = 'liveDepartures';
  const timetables = 'timetables';

  const [selectedTab, setSelectedTab] = useState(journeyPlanner);
  const hideLDAndTT = isJpPreviewOn === 'True';

  useEffect(() => {
    if (window.theme && typeof window.theme.initializeAccordion === 'function') {
      window.theme.initializeAccordion();
    }
  }, []);

  const renderContent = () => {
    switch (selectedTab) {
      case journeyPlanner:
        return <JourneyPlanner jpUrl={getJourneyPlannerUrl(jpUrl)} jpPreviewOn={hideLDAndTT}/>;
      case liveDepartures:
        return <LiveDepartures ldUrl={getLiveDeparturesUrl(ldUrl)} />;
      case timetables:
        return (
          <Timetables
            ttUrl={getTimetablesUrl(ttUrl)}
            dtUrl={getDigitalTimetablesUrl(dtUrl)}
          />
        );
      default:
        return null;
    }
  };

  const allTabs = [
    <Tab
      id={journeyPlanner}
      key={journeyPlanner}
      className="btf-tab at-font__button at-color--shade-shore-20"
      activeClassName="btf-tab--active at-background-color--tint-shore-10 at-color--tint-ocean-90 at-p-5"
    >
      <span className="btf-tab__title">Journey Planner</span>
    </Tab>,
    <Tab
      id={liveDepartures}
      key={liveDepartures}
      className="btf-tab at-font__button at-color--shade-shore-20"
      activeClassName="btf-tab--active at-background-color--tint-shore-10 at-color--tint-ocean-90 at-p-5"
    >
      <span className="btf-tab__title">Live departures</span>
    </Tab>,
    <Tab
      id={timetables}
      key={timetables}
      className="btf-tab at-font__button at-color--shade-shore-20"
      activeClassName="btf-tab--active at-background-color--tint-shore-10 at-color--tint-ocean-90 at-p-5"
    >
      <span className="btf-tab__title">Timetables</span>
    </Tab>,
  ];

  const filteredTabs = allTabs.filter((tab) => {
    const id = tab.props.id;
    return !(hideLDAndTT && (id === liveDepartures || id === timetables));
  });

  return (
    <div className="main at-font">
      <Tabs
        className="btf-tabs at-d-none at-d-sm-flex"
        activeTab={selectedTab}
        onChange={setSelectedTab}
      >
        {filteredTabs}
      </Tabs>
      <div className="content at-mt-7 at-d-none at-d-sm-block">
        <div className="at-flex-grid">
          <div className="row">
            <div className={classnames('col-xs-12', 'form')}>
              { renderContent() }
            </div>
          </div>
        </div>
      </div>
      <div className="at-col-span-100 at-accordion-component at-border-bottom at-d-block at-d-sm-none">
        <h4 className="at-m-0">
          <button
            type="button"
            className={`at-accordion-component__header at-w-100 at-link at-d-flex at-p-4 at-border-top at-border-bottom 
              at-border-left--none at-border-right--none at-border-radius--none at-background-color--white 
              ${selectedTab === journeyPlanner ? 'at-accordion-component__header--expanded at-accordion-component--expanded' : ''}`}
            aria-expanded={selectedTab === journeyPlanner}
            data-element="accordion"
            onClick={() => setSelectedTab(journeyPlanner)}
            data-accordion-target="#accordion-panel-1"
          >
            <div>
              <span className="at-d-flex at-justify-content-center at-align-items-center at-ps-5 at-pt-5 at-pe-6 at-pb-6">
                <svg className="at-accordion-component__icon icon at-position-absolute at-ms-1 at-mt-1">
                  <use href="#icon-chevron-down" />
                </svg>
              </span>
            </div>
            <span className="at-font__text-button--sm at-my-3 at-ms-4 at-align-text-left">
              Journey Planner
            </span>
          </button>
        </h4>
        <div className={`accordion-panel at-pb-4 at-mt-1 ${selectedTab === journeyPlanner ? '' : 'at-d-none'}`} id="accordion-panel-1">
          <JourneyPlanner jpUrl={getJourneyPlannerUrl(jpUrl)} jpPreviewOn={hideLDAndTT} />
        </div>
        {!hideLDAndTT && (
        <>
          <h4 className="at-m-0">
            <button
              type="button"
              className={`at-accordion-component__header at-w-100 at-link at-d-flex at-p-4 at-border-top at-border-bottom 
              at-border-left--none at-border-right--none at-border-radius--none at-background-color--white 
              ${selectedTab === liveDepartures ? 'at-accordion-component__header--expanded at-accordion-component--expanded' : ''}`}
              aria-expanded={selectedTab === liveDepartures}
              data-element="accordion"
              onClick={() => setSelectedTab(liveDepartures)}
              data-accordion-target="#accordion-panel-2"
            >
              <div>
                <span className="at-d-flex at-justify-content-center at-align-items-center at-ps-5 at-pt-5 at-pe-6 at-pb-6">
                  <svg className="at-accordion-component__icon icon at-position-absolute at-ms-1 at-mt-1">
                    <use href="#icon-chevron-down" />
                  </svg>
                </span>
              </div>
              <span className="at-font__text-button--sm at-my-3 at-ms-4 at-align-text-left">
                Live departures
              </span>
            </button>
          </h4>
          <div className={`accordion-panel at-pb-4 at-mt-1 ${selectedTab === liveDepartures ? '' : 'at-d-none'}`} id="accordion-panel-2">
            <LiveDepartures ldUrl={getLiveDeparturesUrl(ldUrl)} />
          </div>
          <h4 className="at-m-0">
            <button
              type="button"
              className={`at-accordion-component__header at-w-100 at-link at-d-flex at-p-4 at-border-top at-border-bottom 
              at-border-left--none at-border-right--none at-border-radius--none at-background-color--white 
              ${selectedTab === timetables ? 'at-accordion-component__header--expanded at-accordion-component--expanded' : ''}`}
              aria-expanded={selectedTab === timetables}
              data-element="accordion"
              onClick={() => setSelectedTab(timetables)}
              data-accordion-target="#accordion-panel-3"
            >
              <div>
                <span className="at-d-flex at-justify-content-center at-align-items-center at-ps-5 at-pt-5 at-pe-6 at-pb-6">
                  <svg className="at-accordion-component__icon icon at-position-absolute at-ms-1 at-mt-1">
                    <use href="#icon-chevron-down" />
                  </svg>
                </span>
              </div>
              <span className="at-font__text-button--sm at-my-3 at-ms-4 at-align-text-left">
                Timetables
              </span>
            </button>
          </h4>
          <div className={`accordion-panel at-pb-4 at-mt-1 ${selectedTab === timetables ? '' : 'at-d-none'}`} id="accordion-panel-3">
            <Timetables
              ttUrl={getTimetablesUrl(ttUrl)}
              dtUrl={getDigitalTimetablesUrl(dtUrl)}
            />
          </div>
        </>
        )}
      </div>
    </div>
  );
}

App.propTypes = {
  jpUrl: PropTypes.string,
  ldUrl: PropTypes.string,
  ttUrl: PropTypes.string,
  dtUrl: PropTypes.string,
  isJpPreviewOn: PropTypes.bool,
};

App.defaultProps = {
  jpUrl: DEFAULT_JP_URL,
  ldUrl: DEFAULT_LD_URL,
  ttUrl: DEFAULT_TT_URL,
  dtUrl: DEFAULT_DT_URL,
  isJpPreviewOn: false,
};

export default App;
